/**
 * Patient Mode Utilities
 * Functions to manage patient read-only mode
 */

import { authService } from '../auth/authService';
import { isPatient } from './roleUtils';
import { STORAGE_KEYS } from '../config/api.config';
import { clearPatientViewLanguage, resetI18nToStaffDefault } from './patientViewLanguage';

/**
 * Check if user is in patient mode
 * This is the single source of truth for patient mode checking
 * Checks both user role and sessionStorage flag for backward compatibility
 */
export function isPatientMode(): boolean {
  // First check if user has patient role (primary check)
  const user = authService.getUser();
  if (user && isPatient(user)) {
    return true;
  }

  // Fallback to sessionStorage check (patient sessions are tab-scoped)
  return sessionStorage.getItem(STORAGE_KEYS.IS_PATIENT_MODE) === 'true';
}

/**
 * Set patient mode in sessionStorage
 */
export function setPatientMode(enabled: boolean): void {
  if (enabled) {
    sessionStorage.setItem(STORAGE_KEYS.IS_PATIENT_MODE, 'true');
  } else {
    sessionStorage.removeItem(STORAGE_KEYS.IS_PATIENT_MODE);
  }
}

/**
 * Clear patient mode (usually on logout)
 */
export function clearPatientMode(): void {
  sessionStorage.removeItem(STORAGE_KEYS.IS_PATIENT_MODE);
  clearPatientViewLanguage();
  resetI18nToStaffDefault();
}

/**
 * Check if a feature should be disabled in patient mode
 */
export function isFeatureDisabledInPatientMode(featureName: string): boolean {
  if (!isPatientMode()) {
    return false;
  }

  // List of features that should be disabled in patient mode
  const disabledFeatures = [
    'measurement',
    'annotation',
    'export',
    'print',
    'download',
    'share',
    'edit',
    'delete',
    'save',
    'report',
  ];

  return disabledFeatures.some(disabled =>
    featureName.toLowerCase().includes(disabled.toLowerCase())
  );
}
